import { useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { applicationApi, jobApi, type JobSearchParams } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { PageHeader } from '@/components/ui/Page'
import { CompanyAvatar } from '@/features/applications/ApplicationsTable'
import { useApplyDialog } from '@/features/applications/ApplyOptionsDialog'
import { refreshLicense } from '@/features/billing/useLicense'
import { cn, formatSalary, humanise, relativeTime } from '@/lib/utils'
import { toast } from '@/store/toast'
import { isLicenseError } from '@/store/license'
import { AddJobModal } from './AddJobModal'
import { JobFilterBar, clearedJobFilters, type JobFilters } from './JobFilterBar'
import { MatchRing } from './JobCard'
import { boardOptionId, sourceFilterFor, sourceOptionId } from './sourceFilter'
import type { ApplyOptions, Job } from '@/types/api'

type Option = { value: string; label: string }

const DEFAULT_FILTERS: JobSearchParams = { searchMode: 'title_description', page: 1, pageSize: 20 }

export function BrowseJobsPage() {
  const queryClient = useQueryClient()
  const [params] = useSearchParams()
  // "View all" on the dashboard opens this pre-filtered to one source; a job
  // portal's job count (Profile → Job Portals) opens it on that portal.
  const [filters, setFilters] = useState<JobSearchParams>(() => ({
    ...DEFAULT_FILTERS,
    sources: params.get('source') ? [params.get('source') as string] : undefined,
    boards: params.get('board') ? [params.get('board') as string] : undefined,
  }))
  const [addOpen, setAddOpen] = useState(false)
  const [busyJobId, setBusyJobId] = useState<string | null>(null)

  // The same list the dashboard's "Pull jobs from" row shows.
  const sourcesQuery = useQuery({
    queryKey: ['job-sources'],
    queryFn: jobApi.sources,
    staleTime: 30_000,
  })

  const jobsQuery = useQuery({
    queryKey: ['jobs', filters],
    queryFn: () => jobApi.search(filters),
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  })

  const applyDialog = useApplyDialog()
  const apply = useMutation({
    mutationFn: ({ jobId, options }: { jobId: string; options: ApplyOptions }) =>
      applicationApi.create({ jobId, ...options }),
    onMutate: ({ jobId }) => setBusyJobId(jobId),
    onSettled: () => setBusyJobId(null),
    onSuccess: (application) => {
      queryClient.invalidateQueries({ queryKey: ['applications'] })
      queryClient.invalidateQueries({ queryKey: ['application-stats'] })
      refreshLicense(queryClient)
      toast.success('Applying', `${application.job.companyName} — we'll flag anything that needs you`)
    },
    onError: (error: ApiError) => {
      if (!isLicenseError(error)) toast.error('Could not apply', error.message)
    },
  })

  /** Any filter change starts again from the first page. */
  const changeFilters = (next: JobFilters) =>
    setFilters({ ...next, page: 1, pageSize: filters.pageSize ?? DEFAULT_FILTERS.pageSize })
  const clearFilters = () => changeFilters(clearedJobFilters({ ...filters, sources: undefined, boards: undefined }))

  // Job portals are their own choices (filtered by `boards`); the rest by `sources`.
  const activeSource = filters.boards?.[0]
    ? boardOptionId(filters.boards[0])
    : filters.sources?.[0] ?? null
  const sourceOptions = useMemo(() => {
    const listed = Array.isArray(sourcesQuery.data) ? sourcesQuery.data : []
    const options: Option[] = listed
      .filter((option) => option.available)
      .map((option) => ({
        value: sourceOptionId(option),
        label: `${option.label} (${option.jobCount})`,
      }))
    // A filter that came in on the URL stays visible even if it is not listed.
    if (activeSource && !options.some((option) => option.value === activeSource)) {
      options.unshift({
        value: activeSource,
        label: humanise(filters.boards?.[0] ?? filters.sources?.[0]),
      })
    }
    return options
  }, [sourcesQuery.data, activeSource, filters.boards, filters.sources])
  const setSource = (id: string | null) => {
    const { sources, boards } = sourceFilterFor(id)
    setFilters((current) => ({ ...current, sources, boards, page: 1 }))
  }

  const page = jobsQuery.data?.pageInfo
  const rangeStart = page ? (page.page - 1) * (filters.pageSize ?? 20) + 1 : 0
  const rangeEnd = page ? Math.min(page.page * (filters.pageSize ?? 20), page.totalItems) : 0

  return (
    <div>
      <PageHeader
        eyebrow="Discover opportunities"
        title="Find your next role"
        description={
          page ? (
            <>
              <span className="num font-medium text-ink-soft">{page.totalItems.toLocaleString()}</span>
              {` job${page.totalItems === 1 ? '' : 's'}`}
              {filters.q ? ` matching “${filters.q}”` : ' from your connected sources'}
            </>
          ) : (
            'Explore opportunities from your connected sources. Filter by role, location, and what matters to you.'
          )
        }
        actions={
          <Button variant="secondary" size="sm" icon={<LinkIcon />} onClick={() => setAddOpen(true)}>
            Add your own link
          </Button>
        }
      />

      <div className="mb-4">
        <JobFilterBar
          value={filters}
          onChange={changeFilters}
          searching={jobsQuery.isFetching}
          leading={<SourceChip options={sourceOptions} value={activeSource} onChange={setSource} />}
        />
      </div>

      <section className={cn(jobsQuery.isPlaceholderData && 'opacity-60 transition-opacity')}>
        {jobsQuery.isLoading ? (
          <div className="card divide-y divide-line overflow-hidden">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="flex items-center gap-3.5 px-4 py-4 sm:px-5">
                <Skeleton className="h-9 w-9 rounded-[9px]" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : jobsQuery.isError ? (
          <ErrorState onRetry={() => jobsQuery.refetch()} />
        ) : (jobsQuery.data?.data.length ?? 0) === 0 ? (
          <div className="card">
            <EmptyState
              title="No jobs match those filters"
              description="Try widening the date range or clearing a filter."
              action={
                <Button variant="secondary" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          </div>
        ) : (
          <ul className="card divide-y divide-line overflow-hidden">
            {jobsQuery.data!.data.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                busy={busyJobId === job.id}
                onApply={() =>
                  applyDialog.ask([job], (options) => apply.mutate({ jobId: job.id, options }))
                }
              />
            ))}
          </ul>
        )}

        {page && page.totalPages > 1 && (
          <nav className="mt-4 flex items-center justify-between gap-2" aria-label="Pagination">
            <Button
              variant="secondary"
              size="sm"
              className="h-11 sm:h-9"
              disabled={!page.hasPrevious}
              onClick={() => setFilters((c) => ({ ...c, page: (c.page ?? 1) - 1 }))}
            >
              Prev
            </Button>
            <p className="min-w-0 truncate text-center text-[13px] text-ink-muted">
              Page <span className="num text-ink">{page.page}</span> of <span className="num">{page.totalPages}</span>
              <span className="hidden sm:inline">
                {' '}
                · <span className="num">{rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()}</span> of{' '}
                <span className="num">{page.totalItems.toLocaleString()}</span>
              </span>
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="h-11 sm:h-9"
              disabled={!page.hasNext}
              onClick={() => setFilters((c) => ({ ...c, page: (c.page ?? 1) + 1 }))}
            >
              Next
            </Button>
          </nav>
        )}
      </section>

      <AddJobModal open={addOpen} onClose={() => setAddOpen(false)} />
      {applyDialog.element}
    </div>
  )
}

/** Where jobs came from: a native select styled as a chip, so phones get their own picker. */
function SourceChip({
  options,
  value,
  onChange,
}: {
  options: Option[]
  value: string | null
  onChange: (value: string | null) => void
}) {
  const selected = options.find((option) => option.value === value)
  return (
    <label
      className={cn(
        'relative inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-pill border pl-3 pr-2.5 text-[13px] transition-colors',
        value ? 'border-ink bg-ink text-oncolor' : 'border-line bg-surface text-ink-soft hover:border-line-strong hover:text-ink',
      )}
    >
      <span className="max-w-[180px] truncate">{selected ? `Source: ${selected.label}` : 'Source'}</span>
      <ChevronDownIcon />
      <select
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || null)}
        aria-label="Source"
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        <option value="">All sources</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  )
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/**
 * One job: avatar · title · company · location · meta chips, with the posting
 * link and Apply at the right end (under the text on phones). `score` is
 * shown as a ring when the caller has one.
 */
function JobRow({
  job,
  onApply,
  busy,
  score,
}: {
  job: Job
  onApply: () => void
  busy: boolean
  score?: number
}) {
  const salary = formatSalary(job.minSalary, job.maxSalary, job.salaryCurrency)
  const experience =
    job.minExperienceYears != null
      ? job.maxExperienceYears != null && job.maxExperienceYears !== job.minExperienceYears
        ? `${job.minExperienceYears}–${job.maxExperienceYears} yrs`
        : `${job.minExperienceYears}+ yrs`
      : null
  const posted = job.postedAt ? relativeTime(job.postedAt) : null

  return (
    <li className="flex flex-col gap-2.5 px-4 py-3.5 transition-colors hover:bg-surface-muted/50 sm:flex-row sm:items-center sm:gap-4 sm:px-5">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <CompanyAvatar name={job.companyName} logoUrl={job.company?.logoUrl} />
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug text-ink">{job.title}</h3>
          <p className="mt-0.5 truncate text-[13px] text-ink-soft">
            <span className="font-medium">{job.companyName}</span>
            {job.location ? <span className="text-ink-muted"> · {job.location}</span> : null}
          </p>
          <div className="hscroll mt-2 items-center gap-1.5">
            {job.easyApply && <MetaChip tone="brand">Easy apply</MetaChip>}
            {job.board?.name && <MetaChip title={`From ${job.board.name}`}>{job.board.name}</MetaChip>}
            {salary && <MetaChip className="num">{salary}</MetaChip>}
            {experience && <MetaChip className="num">{experience}</MetaChip>}
            {job.workplaceType && <MetaChip className="capitalize">{job.workplaceType}</MetaChip>}
            {posted && <span className="shrink-0 whitespace-nowrap text-[12px] text-ink-faint">{posted}</span>}
          </div>
        </div>
        {score != null && <MatchRing score={score} size={40} />}
      </div>

      <div className="flex shrink-0 items-center justify-end gap-1 pl-12 sm:pl-0">
        <a
          href={job.applyUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View posting for ${job.title} at ${job.companyName}`}
          title="View posting"
          className="grid h-9 w-9 place-items-center rounded-field text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
        >
          <ExternalIcon />
        </a>
        <Button size="sm" className="tap min-w-[84px]" onClick={onApply} loading={busy}>
          Apply
        </Button>
      </div>
    </li>
  )
}

function MetaChip({
  children,
  className,
  tone = 'neutral',
  title,
}: {
  children: ReactNode
  className?: string
  tone?: 'neutral' | 'brand'
  title?: string
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-[6px] px-2 py-0.5 text-[12px]',
        tone === 'brand' ? 'bg-brand-soft font-medium text-brand' : 'bg-surface-muted text-ink-soft',
        className,
      )}
    >
      {children}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function LinkIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M6.5 9.5a3 3 0 0 0 4.2 0l2-2a3 3 0 0 0-4.2-4.2l-.8.8M9.5 6.5a3 3 0 0 0-4.2 0l-2 2a3 3 0 0 0 4.2 4.2l.8-.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ExternalIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M6.5 3.5h-2a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1v-2M9.5 3h3.5v3.5M13 3 7.5 8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ChevronDownIcon() {
  return (
    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="m3 4.5 3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}
